/**
 * KAN-754: действия владельца в карточке ручного товара: "Редактировать",
 * "Снять с продажи" / "Вернуть в продажу", "Удалить" с подтверждением.
 * У товаров из фида не показывается.
 */
import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { useCatalogMutations } from '@/hooks/useCatalog';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { CatalogProductDetail } from '@/types/catalog';
import { ProductFormDialog } from './ProductFormDialog';

interface Props {
  product: CatalogProductDetail;
  /** Товар удалён: уйти из карточки. */
  onDeleted: () => void;
}

export function ManualProductActions({ product, onDeleted }: Props) {
  const { updateProduct, deleteProduct } = useCatalogMutations();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!product.manual) return null;

  const pending = updateProduct.isPending || deleteProduct.isPending;
  const error = updateProduct.error ?? deleteProduct.error;

  const toggleAvailable = (): void =>
    updateProduct.mutate({ id: product.id, body: { available: !product.available } });

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={pending} onClick={() => setEditOpen(true)}>
          <Pencil className="mr-1.5 h-4 w-4" />
          Редактировать
        </Button>
        <Button size="sm" variant="outline" disabled={pending} onClick={toggleAvailable}>
          {product.available ? 'Снять с продажи' : 'Вернуть в продажу'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-red-600 hover:text-red-700"
          disabled={pending}
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2 className="mr-1.5 h-4 w-4" />
          Удалить
        </Button>
      </div>
      {error && <p className="text-xs text-red-700">{extractErrorMessage(error, 'Не удалось изменить товар')}</p>}
      <ProductFormDialog open={editOpen} onOpenChange={setEditOpen} product={product} />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        testId="product-delete-confirm"
        view={{
          title: `Удалить товар "${product.name}"?`,
          description: 'Товар пропадёт из каталога. Действие нельзя отменить.',
          confirm: 'Удалить',
        }}
        onConfirm={() => deleteProduct.mutate(product.id, { onSuccess: onDeleted })}
      />
    </div>
  );
}
